import { toast } from 'sonner';
import { createMutationHook } from '@/hooks/lib/createMutationHook';
import { apiPost, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';

/**
 * Response shape returned by `POST /users/:id/resend-invitation`. The
 * backend mints a fresh single-use invitation token and re-sends the
 * invitation email; `invitationSent` mirrors the createUser response so
 * the UI can pick the right toast for transient SendGrid failures.
 */
export interface ResendInvitationResponse {
  userId: string;
  email: string;
  invitationSent: boolean;
}

/**
 * Re-trigger the invitation email for a user still in `invited` status.
 * The backend rejects the call with 400 if the user already accepted the
 * prior invite — the FE only renders the button when status is invited,
 * but the server-side check is still the source of truth.
 */
export const useResendInvitation = createMutationHook<ResendInvitationResponse, string>({
  mutationFn: (userId) =>
    apiPost<ResendInvitationResponse, undefined>(
      operationsClient,
      settingsEndpoints.resendInvitation(userId),
      undefined,
    ),
  onSuccess: (data, userId, queryClient) => {
    // Refetch the users list so the "Invitation sent" pill reflects the
    // new `invitedAt` timestamp — without this the relative-time tooltip
    // would keep showing the stale send.
    void queryClient.invalidateQueries({ queryKey: settingsKeys.users() });
    // Also refresh the open user-detail card, which reads userDetail(userId).
    void queryClient.invalidateQueries({ queryKey: settingsKeys.userDetail(userId) });
    if (data.invitationSent) {
      toast.success(`Invitation email resent to ${data.email}`);
    } else {
      // The token was minted but the email failed to send. Surface that
      // distinct case so the admin can retry rather than assume success.
      toast.error('Invitation email could not be sent. Try again in a moment.');
    }
  },
});
